import React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { CarPageList } from './list';
import { TCar } from '../start-work-order/type';
import { setLoading } from '../../redux/slices/appInfo';
import { getCarsPaging } from '../../api/setting/cars';
import {
    Direction,
    IPagination,
    SearchQueryParams,
} from '../../interface/paging';
import { DEFAULT_PAGE_SIZE } from '../../utils/constants';
import { SearchParamsStateType, useFilter } from '../../hooks/useFilter';
import { removeUndefinedAttribute } from '../../utils';

const Wrapper = styled.div``;

type TSortData = {
    field?: string;
    order?: 'asc' | 'desc';
};
type TSortDataContext = {
    sortData: TSortData;
    setSortData: (data: TSortData) => void;
};
const SortDataContext = React.createContext<TSortDataContext>(
    {} as TSortDataContext
);
export const useSortData = () => React.useContext(SortDataContext);

export const CarsPage: React.FC = () => {
    const dispatch = useDispatch();
    const filtersDefaults: SearchParamsStateType = {
        page: { type: 'number', default: 1 },
        pageSize: { type: 'number', default: DEFAULT_PAGE_SIZE },
        sortBy: { type: 'string', default: Direction.DESC },
        orderBy: { type: 'string', default: 'createdAt' },
    };
    const [filterParams, setFilterParams] = useFilter(filtersDefaults);
    const [pagination, setPagination] = React.useState<IPagination | undefined>(
        undefined
    );
    const [sortData, setSortData] = React.useState<TSortData>({
        field: 'createdAt',
        order: 'desc',
    });
    const [carList, setCarList] = React.useState<TCar[]>([]);

    const onChangeSort = React.useCallback(
        (data: TSortData) => {
            setSortData(data);
            const searchQueryParams: SearchQueryParams = {
                page: 1,
                pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
                sortBy: data.order as Direction | Direction.DESC,
                orderBy: data.field,
            };
            onLoadData(searchQueryParams);
        },
        [filterParams]
    );

    const onLoadData = React.useCallback(
        async (queryParams: SearchQueryParams | undefined) => {
            try {
                dispatch(setLoading(true));
                const newPayload = removeUndefinedAttribute(queryParams);
                setFilterParams(newPayload);
                const result = await getCarsPaging(newPayload);
                if (result.status === 200) {
                    setCarList(result.data?.data ?? []);
                    setPagination({
                        page: result.data?.currentPage ?? 1,
                        pageSize: result.data?.pageSize ?? DEFAULT_PAGE_SIZE,
                        totalCount: result.data?.totalRecords ?? 0,
                        totalPage: result.data?.totalPages ?? 0,
                        order: Direction.DESC,
                        orderBy: newPayload?.orderBy,
                    });
                }
            } catch (error) {
                // Handle error
            } finally {
                dispatch(setLoading(false));
            }
        },
        [dispatch]
    );

    const onChangePaging = React.useCallback(
        (values: any) => {
            const searchQueryParams: SearchQueryParams = {
                page: values.page,
                pageSize: pagination?.pageSize ?? 10,
                sortBy: pagination?.order as Direction | Direction.DESC,
                orderBy: pagination?.orderBy ?? 'createdAt',
            };
            onLoadData(searchQueryParams);
        },
        [pagination]
    );

    React.useEffect(() => {
        onLoadData({
            page: 1,
            pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
            sortBy: Direction.DESC,
            orderBy: 'createdAt',
        });
    }, []);

    const onReload = React.useCallback(() => {
        onLoadData({
            page: 1,
            pageSize: filterParams?.pageSize || DEFAULT_PAGE_SIZE,
            sortBy: Direction.DESC,
            orderBy: filterParams.orderBy,
        });
    }, [filterParams]);

    return (
        <Wrapper className="flex flex-col items-center justify-start h-full">
            <div className="flex flex-row items-start h-full w-full">
                <SortDataContext.Provider
                    value={{ sortData, setSortData: onChangeSort }}
                >
                    <CarPageList
                        carList={carList}
                        pagination={pagination}
                        onChangePaging={onChangePaging}
                        onReload={onReload}
                        filterParams={filterParams}
                        onFilterData={onLoadData}
                    />
                </SortDataContext.Provider>
            </div>
        </Wrapper>
    );
};
